// Email command center — Klaviyo campaigns, deliverability guardrails, and
// AI drafting. Data comes from /api/marketing/email/overview, which evaluates
// each sent campaign against the warm-up thresholds (bounce <2%, spam <0.1%
// with a 0.3% pause line, opens >20%, unsubs <0.5%) so the health banner can
// say "keep going" or "pause the next send" without anyone reading raw rates.
import { useCallback, useEffect, useState } from "react";
import {
  Mail,
  Users,
  Send,
  Clock,
  CheckCircle2,
  AlertTriangle,
  OctagonAlert,
  Sparkles,
  Copy,
  Check,
  RotateCcw,
  RefreshCw,
  ChevronDown,
  ExternalLink,
  CalendarClock,
  Eye,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { HistoryDrawer, type HistoryDraft } from "./HistoryDrawer";
import { EmailResults } from "./EmailResults";

// ─── Types (mirror apps/api/src/routes/marketing-email.ts) ────────────────────

interface KlaviyoList {
  id: string;
  name: string;
  profileCount: number | null;
}

interface CampaignStats {
  recipients: number | null;
  delivered: number | null;
  deliveryRate: number | null;
  openRate: number | null;
  opensUnique: number | null;
  clickRate: number | null;
  clicksUnique: number | null;
  bounceRate: number | null;
  unsubscribeRate: number | null;
  spamComplaintRate: number | null;
}

type HealthLevel = "good" | "watch" | "pause";
// "delayed" = campaigns were sent but stats didn't come back (Klaviyo rate
// limit, no cache). Surfaced instead of a false "good" so the pause guardrail
// can't silently disappear.
type OverallLevel = HealthLevel | "delayed";

interface HealthFlag {
  metric: string;
  level: "watch" | "pause";
  message: string;
}

interface KlaviyoCampaign {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  scheduledAt: string | null;
  sentAt: string | null;
  audienceNames: string[];
  stats: CampaignStats | null;
  health: { level: HealthLevel; flags: HealthFlag[] } | null;
}

interface Overview {
  connected: boolean;
  totalSubscribers?: number;
  lists?: KlaviyoList[];
  campaigns?: KlaviyoCampaign[];
  upcoming?: KlaviyoCampaign[];
  overall?: { level: OverallLevel; flags: HealthFlag[] };
  /** Whether any campaign stats came back this response. */
  statsAvailable?: boolean;
  /** Whether the stats shown are last-good (served through a rate-limited refresh). */
  statsStale?: boolean;
  warnings?: string[];
  fetchedAt?: string;
}

interface EmailVariant {
  subjectLine: string;
  previewText: string;
  body: string;
}

type Tone = "professional" | "casual" | "bold";

function coerceTone(t: string): Tone {
  return t === "casual" || t === "bold" ? t : "professional";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  sent: "bg-green-50 text-green-700",
  scheduled: "bg-blue-50 text-blue-700",
  sending: "bg-blue-50 text-blue-700",
  draft: "bg-cream-100 text-brown-500",
  cancelled: "bg-cream-100 text-brown-400",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtNumber(n: number) {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function fmtPct(rate: number | null) {
  if (rate == null) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}

// A campaign counts as "sent" from its status only — never from stats presence
// (so a rate-limited stats fetch can't drop the count to 0 or hide the banner)
// and never from sentAt, which Klaviyo also sets for scheduled sends. Mirrors
// isSentCampaign in the API route.
function isSent(c: KlaviyoCampaign): boolean {
  return c.status.toLowerCase().includes("sent");
}

// Warnings about the campaign-values-reports call get plain-language treatment
// instead of the raw API error (which is JSON). Everything else stays verbose.
function isStatsWarning(w: string): boolean {
  return /campaign (stats|performance)/i.test(w);
}

// Threshold coloring for the performance table. Rates are fractions.
function rateTone(
  metric: "open" | "bounce" | "unsub" | "spam",
  rate: number | null,
): string {
  if (rate == null) return "text-brown-300";
  switch (metric) {
    case "open":
      // 0% usually means "just sent, no data yet" — stay neutral (matches
      // the backend's evaluateHealth, which only flags openRate > 0).
      if (rate === 0) return "text-brown-300";
      return rate >= 0.2 ? "text-green-700" : "text-amber-600";
    case "bounce":
      return rate < 0.02 ? "text-brown-500" : "text-amber-600";
    case "unsub":
      return rate < 0.005 ? "text-brown-500" : "text-amber-600";
    case "spam":
      if (rate >= 0.003) return "text-rust-600 font-bold";
      return rate < 0.001 ? "text-brown-500" : "text-amber-600";
  }
}

// ─── Setup card (not connected) ───────────────────────────────────────────────

function SetupCard() {
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F4F7FF]">
          <KlaviyoLogo />
        </div>
        <div>
          <h3 className="font-display font-bold text-brown-500">
            Connect Klaviyo
          </h3>
          <p className="text-xs text-brown-400">Email marketing platform</p>
        </div>
      </div>

      <p className="text-sm text-brown-400 mb-6">
        Add your Klaviyo Private API key as{" "}
        <code className="rounded bg-cream-100 px-1.5 py-0.5 text-xs font-mono text-brown-500">
          KLAVIYO_API_KEY
        </code>{" "}
        in Railway to connect your email list and campaigns.
      </p>

      <ol className="space-y-3 mb-6">
        {[
          "In Klaviyo, go to Settings → API Keys",
          'Click "Create Private API Key" — Full Access',
          "Copy the key",
          "In Railway → your service → Variables, add KLAVIYO_API_KEY",
          "Reload this page",
        ].map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-peach-100 text-xs font-bold text-brown-500">
              {i + 1}
            </span>
            <p className="text-sm text-brown-500">{step}</p>
          </li>
        ))}
      </ol>

      <a
        href="https://www.klaviyo.com/account#api-keys-tab"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-brown-400 hover:text-brown-500"
      >
        Open Klaviyo API Keys
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

// ─── Deliverability health banner ─────────────────────────────────────────────

function HealthBanner({
  overall,
  stale,
}: {
  overall: { level: OverallLevel; flags: HealthFlag[] };
  stale?: boolean;
}) {
  // Sent, but stats didn't refresh — never claim "healthy" without the numbers.
  if (overall.level === "delayed") {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm font-bold text-amber-800">
            Performance data delayed — check before sending.
          </p>
        </div>
        <p className="mt-1.5 pl-6 text-sm text-amber-800">
          We couldn't refresh deliverability from Klaviyo (rate limit). Recent
          sends may still have a guardrail issue — review the numbers in Klaviyo
          before your next send.
        </p>
      </div>
    );
  }

  if (overall.level === "good") {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-green-200 bg-green-50 px-5 py-3.5">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-700" />
        <p className="text-sm font-semibold text-green-800">
          Deliverability is healthy — all recent sends are inside the warm-up
          guardrails. Clear to keep sending.
          {stale && (
            <span className="font-normal text-green-700">
              {" "}
              (based on last refresh)
            </span>
          )}
        </p>
      </div>
    );
  }

  const pause = overall.level === "pause";
  // De-dupe repeated flags (same message can come from multiple campaigns).
  const messages = Array.from(new Set(overall.flags.map((f) => f.message)));

  return (
    <div
      className={cn(
        "rounded-lg border px-5 py-4",
        pause
          ? "border-rust-300 bg-rust-50"
          : "border-amber-300 bg-amber-50",
      )}
    >
      <div className="flex items-center gap-2.5">
        {pause ? (
          <OctagonAlert className="h-4 w-4 shrink-0 text-rust-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        )}
        <p
          className={cn(
            "text-sm font-bold",
            pause ? "text-rust-700" : "text-amber-800",
          )}
        >
          {pause
            ? "Pause before the next send — a guardrail was crossed."
            : "Deliverability watch — some metrics are outside target."}
        </p>
      </div>
      <ul className="mt-2 space-y-1 pl-6">
        {messages.map((m) => (
          <li
            key={m}
            className={cn(
              "list-disc text-sm",
              pause ? "text-rust-700" : "text-amber-800",
            )}
          >
            {m}
          </li>
        ))}
      </ul>
      {stale && (
        <p
          className={cn(
            "mt-2 pl-6 text-xs",
            pause ? "text-rust-600" : "text-amber-700",
          )}
        >
          Based on the last refresh — Klaviyo's live numbers are rate-limited
          right now.
        </p>
      )}
    </div>
  );
}

// ─── Field with copy button ─────────────────────────────────────────────────────

function CopyField({
  label,
  value,
  fieldKey,
  copied,
  onCopy,
  emphasis,
  multiline,
}: {
  label: string;
  value: string;
  fieldKey: string;
  copied: string | null;
  onCopy: (text: string, key: string) => void;
  emphasis?: boolean;
  multiline?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] font-bold uppercase tracking-wide text-brown-400">
          {label}
        </p>
        <button
          onClick={() => onCopy(value, fieldKey)}
          className="flex items-center gap-1 text-xs text-brown-400 hover:text-brown-500"
        >
          {copied === fieldKey ? (
            <>
              <Check className="h-3 w-3 text-green-600" />
              <span className="text-green-600">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              Copy
            </>
          )}
        </button>
      </div>
      {multiline ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-brown-500">
          {value}
        </p>
      ) : (
        <p
          className={cn(
            "text-sm",
            emphasis ? "font-semibold text-brown-500" : "text-brown-400",
          )}
        >
          {value}
        </p>
      )}
    </div>
  );
}

// ─── Draft panel ──────────────────────────────────────────────────────────────

function DraftPanel({
  lists,
  initial,
}: {
  lists: KlaviyoList[];
  initial?: HistoryDraft;
}) {
  // Derive initial state from a loaded history entry, when present.
  const initialInputs = (initial?.inputs ?? {}) as {
    listName?: unknown;
    variants?: unknown;
  };
  const initialListId =
    typeof initialInputs.listName === "string"
      ? lists.find((l) => l.name === initialInputs.listName)?.id
      : undefined;
  const initialVariantValues = Array.isArray(initial?.output)
    ? (initial!.output as EmailVariant[])
    : [];

  const [open, setOpen] = useState(Boolean(initial));
  const [listId, setListId] = useState(initialListId ?? lists[0]?.id ?? "");
  const [topic, setTopic] = useState(initial?.topic ?? "");
  const [tone, setTone] = useState<Tone>(
    initial ? coerceTone(initial.tone) : "professional",
  );
  const [variants, setVariants] = useState<number>(
    typeof initialInputs.variants === "number"
      ? Math.min(Math.max(initialInputs.variants, 1), 3)
      : 3,
  );
  const [draftVariants, setDraftVariants] =
    useState<EmailVariant[]>(initialVariantValues);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const selectedList = lists.find((l) => l.id === listId);
  const draft = draftVariants[active];

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api<{ variants?: EmailVariant[] }>(
        "/api/marketing/email/draft",
        {
          method: "POST",
          body: JSON.stringify({
            listName: selectedList?.name ?? "All Customers",
            topic: topic.trim(),
            tone,
            variants,
          }),
        },
      );
      setDraftVariants(Array.isArray(data.variants) ? data.variants : []);
      setActive(0);
    } catch (e) {
      setError(apiErrorMessage(e, "Draft failed"));
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const hasVariants = draftVariants.length > 1;

  return (
    <div className="rounded-lg border border-brown-100 bg-white overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-6 py-4 hover:bg-cream-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-ink underline underline-offset-4" />
          <span className="font-display font-bold text-brown-500">
            Draft a campaign with AI
          </span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-brown-400 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="border-t border-brown-50 px-6 py-5 space-y-4">
          {/* Audience */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
              Audience
            </label>
            <select
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="w-full rounded-md border border-brown-200 bg-cream-50 px-3 py-2 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-1 focus:ring-peach-400"
            >
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.profileCount ? ` (${fmtNumber(l.profileCount)})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Topic */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
              What's this campaign about?
            </label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Summer gifting season is here — we're featuring custom branded apparel for corporate events."
              rows={3}
              className="w-full rounded-md border border-brown-200 bg-cream-50 px-4 py-3 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none focus:ring-1 focus:ring-peach-400 resize-none"
            />
          </div>

          {/* Tone + Variations */}
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
                Tone
              </label>
              <div className="flex gap-2">
                {(["professional", "casual", "bold"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTone(t)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-sm font-semibold capitalize transition-colors",
                      tone === t
                        ? "border-peach-400 bg-peach-50 text-brown-500"
                        : "border-brown-200 bg-white text-brown-400 hover:border-brown-300",
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
                Variations
              </label>
              <div className="flex gap-2">
                {([1, 2, 3] as const).map((n) => (
                  <button
                    key={n}
                    onClick={() => setVariants(n)}
                    title={`${n} variation${n > 1 ? "s" : ""}`}
                    className={cn(
                      "h-9 w-9 rounded-md border text-sm font-semibold transition-colors",
                      variants === n
                        ? "border-peach-400 bg-peach-50 text-brown-500"
                        : "border-brown-200 bg-white text-brown-400 hover:border-brown-300",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={generate}
              disabled={!topic.trim() || loading}
              className={cn(
                "flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors",
                topic.trim() && !loading
                  ? "bg-peach-400 text-white hover:bg-peach-500"
                  : "bg-cream-200 text-brown-300 cursor-not-allowed",
              )}
            >
              <Sparkles className={cn("h-4 w-4", loading && "animate-pulse")} />
              {loading ? "Drafting…" : "Draft campaign"}
            </button>
            {draftVariants.length > 0 && !loading && (
              <button
                onClick={generate}
                className="flex items-center gap-1.5 text-sm font-semibold text-brown-400 hover:text-brown-500"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Regenerate
              </button>
            )}
          </div>

          {error && <p className="text-sm text-rust-600 font-medium">{error}</p>}

          {/* Draft output */}
          {draft && (
            <div className="mt-2 space-y-3 rounded-md border border-brown-100 bg-cream-50 p-5">
              {/* Variant switcher */}
              {hasVariants && (
                <div className="flex items-center gap-2 pb-1">
                  {draftVariants.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className={cn(
                        "rounded-md px-3 py-1 text-xs font-semibold transition-colors",
                        active === i
                          ? "bg-peach-400 text-white"
                          : "bg-white text-brown-400 hover:bg-cream-100 border border-brown-100",
                      )}
                    >
                      Variation {i + 1}
                    </button>
                  ))}
                </div>
              )}

              <CopyField
                label="Subject line"
                value={draft.subjectLine}
                fieldKey="subject"
                copied={copied}
                onCopy={copy}
                emphasis
              />
              <CopyField
                label="Preview text"
                value={draft.previewText}
                fieldKey="preview"
                copied={copied}
                onCopy={copy}
              />
              <CopyField
                label="Email body"
                value={draft.body}
                fieldKey="body"
                copied={copied}
                onCopy={copy}
                multiline
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Campaign performance table ───────────────────────────────────────────────

function CampaignTable({ campaigns }: { campaigns: KlaviyoCampaign[] }) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white overflow-hidden">
      <div className="border-b border-brown-50 px-6 py-4">
        <h3 className="font-display font-bold text-brown-500">
          Campaign performance
        </h3>
        <p className="mt-0.5 text-xs text-brown-400">
          Targets: opens &gt;20% · bounces &lt;2% · unsubscribes &lt;0.5% ·
          spam &lt;0.1%
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-brown-50">
              {[
                "Campaign",
                "Status",
                "Sent",
                "Recipients",
                "Opens",
                "Clicks",
                "Bounces",
                "Unsubs",
                "Spam",
              ].map((h, i) => (
                <th
                  key={h}
                  className={cn(
                    "px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brown-300 first:pl-6 last:pr-6",
                    i < 3 ? "text-left" : "text-right",
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, i) => (
              <tr
                key={c.id}
                className={cn(
                  "border-b border-brown-50 last:border-0",
                  i % 2 === 0 ? "bg-white" : "bg-cream-50/40",
                )}
              >
                <td className="px-4 py-3 pl-6">
                  <p className="text-sm font-medium text-brown-500">{c.name}</p>
                  {c.audienceNames.length > 0 && (
                    <p className="text-xs text-brown-300">
                      {c.audienceNames.join(", ")}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-semibold capitalize whitespace-nowrap",
                      STATUS_STYLES[c.status.toLowerCase()] ??
                        "bg-cream-100 text-brown-400",
                    )}
                  >
                    {c.status.toLowerCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-brown-400 whitespace-nowrap">
                  {c.sentAt
                    ? fmtDate(c.sentAt)
                    : c.scheduledAt
                      ? fmtDate(c.scheduledAt)
                      : "—"}
                </td>
                <td className="px-4 py-3 text-right text-sm text-brown-500">
                  {c.stats?.recipients != null
                    ? fmtNumber(c.stats.recipients)
                    : "—"}
                </td>
                <td
                  className={cn(
                    "px-4 py-3 text-right text-sm font-semibold",
                    rateTone("open", c.stats?.openRate ?? null),
                  )}
                >
                  {fmtPct(c.stats?.openRate ?? null)}
                </td>
                <td className="px-4 py-3 text-right text-sm text-brown-500">
                  {fmtPct(c.stats?.clickRate ?? null)}
                </td>
                <td
                  className={cn(
                    "px-4 py-3 text-right text-sm",
                    rateTone("bounce", c.stats?.bounceRate ?? null),
                  )}
                >
                  {fmtPct(c.stats?.bounceRate ?? null)}
                </td>
                <td
                  className={cn(
                    "px-4 py-3 text-right text-sm",
                    rateTone("unsub", c.stats?.unsubscribeRate ?? null),
                  )}
                >
                  {fmtPct(c.stats?.unsubscribeRate ?? null)}
                </td>
                <td
                  className={cn(
                    "px-4 py-3 pr-6 text-right text-sm",
                    rateTone("spam", c.stats?.spamComplaintRate ?? null),
                  )}
                >
                  {fmtPct(c.stats?.spamComplaintRate ?? null)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function EmailTab() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // S5 review: once the saved Results are on, they carry the ONE deliverability verdict and the
  // campaign numbers — the older live tiles, banner and campaign table step aside.
  const [resultsReady, setResultsReady] = useState(false);
  // A loaded history entry + a nonce to force the DraftPanel to remount and
  // re-initialize from it.
  const [loaded, setLoaded] = useState<{ draft: HistoryDraft; nonce: number } | null>(
    null,
  );

  const load = useCallback(async (refresh: boolean) => {
    if (refresh) setRefreshing(true);
    setError(null);
    try {
      const data = await api<Overview>(
        `/api/marketing/email/overview${refresh ? "?refresh=1" : ""}`,
      );
      setOverview(data);
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to load"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-peach-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-rust-600 font-medium">
        Failed to load: {error}
      </p>
    );
  }

  if (!overview?.connected) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-display text-lg font-bold text-brown-500">
            Email campaigns
          </h2>
          <p className="mt-0.5 text-sm text-brown-400">
            Connect Klaviyo to manage your 30k+ customer list from the hub.
          </p>
        </div>
        <SetupCard />
      </div>
    );
  }

  const {
    totalSubscribers = 0,
    lists = [],
    campaigns = [],
    upcoming = [],
    overall,
    statsStale = false,
    warnings = [],
  } = overview;

  // Sent count comes from campaign status / send time, not stats presence — a
  // rate-limited stats fetch must never drop it to 0.
  const sentCampaigns = campaigns.filter(isSent);
  // Stats/performance warnings get a plain-language line; the rest stay verbose.
  const statsWarnings = warnings.filter(isStatsWarning);
  const otherWarnings = warnings.filter((w) => !isStatsWarning(w));
  const openRates = sentCampaigns
    .map((c) => c.stats?.openRate)
    .filter((r): r is number => r != null);
  const avgOpenRate =
    openRates.length > 0
      ? openRates.reduce((a, b) => a + b, 0) / openRates.length
      : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-brown-500">
            Email campaigns
          </h2>
          <p className="mt-0.5 text-sm text-brown-400">Powered by Klaviyo</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => void load(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
            />
            Refresh
          </button>
          <button
            onClick={() => setHistoryOpen(true)}
            className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors"
          >
            <Clock className="h-3.5 w-3.5" />
            History
          </button>
          <a
            href="https://www.klaviyo.com/campaigns"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors"
          >
            Open Klaviyo
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Connected
          </div>
        </div>
      </div>

      {/* Campaign-performance notice — plain language, never the raw 429 JSON.
          The raw API error stays in the server logs; here the operator just
          needs to know the numbers are refreshing (or delayed). */}
      {statsWarnings.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-5 py-3.5">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm font-semibold text-amber-800">
            {statsStale
              ? "Campaign performance is refreshing — Klaviyo rate limit; showing last known numbers."
              : "Campaign performance data is delayed — Klaviyo's rate limit. Check in Klaviyo before your next send."}
          </p>
        </div>
      )}

      {/* Other partial-data notices — some Klaviyo queries failed; sections may
          be incomplete. These keep the verbose per-query error for debugging. */}
      {otherWarnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-5 py-3.5">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Some Klaviyo data couldn't be loaded — showing what we have.
          </p>
          <ul className="mt-1.5 space-y-0.5 pl-6">
            {otherWarnings.map((w) => (
              <li key={w} className="list-disc break-all text-xs text-amber-800">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Deliverability guardrails — gated on sent campaigns (from status, not
          stats), so a rate-limited refresh can never hide the pause banner. */}
      {!resultsReady && overall && sentCampaigns.length > 0 && (
        <HealthBanner overall={overall} stale={statsStale} />
      )}

      {resultsReady && (
        <p className="text-label text-ink-muted">
          {fmtNumber(totalSubscribers)} subscribers across {lists.length} list{lists.length !== 1 ? "s" : ""} · {upcoming.length} scheduled send{upcoming.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Stats row (before the saved Results are on) */}
      {!resultsReady && <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-brown-100 bg-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-brown-300" />
            <span className="text-xs font-semibold uppercase tracking-wide text-brown-300">
              Subscribers
            </span>
          </div>
          <p className="font-display text-2xl font-semibold text-brown-500">
            {fmtNumber(totalSubscribers)}
          </p>
          <p className="mt-0.5 text-xs text-brown-400">
            across {lists.length} list{lists.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="rounded-lg border border-brown-100 bg-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="h-4 w-4 text-brown-300" />
            <span className="text-xs font-semibold uppercase tracking-wide text-brown-300">
              Avg open rate
            </span>
          </div>
          <p className="font-display text-2xl font-semibold text-brown-500">
            {avgOpenRate != null ? fmtPct(avgOpenRate) : "—"}
          </p>
          <p className="mt-0.5 text-xs text-brown-400">
            across {sentCampaigns.length} sent campaign
            {sentCampaigns.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="rounded-lg border border-brown-100 bg-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <Send className="h-4 w-4 text-brown-300" />
            <span className="text-xs font-semibold uppercase tracking-wide text-brown-300">
              Sent
            </span>
          </div>
          <p className="font-display text-2xl font-semibold text-brown-500">
            {sentCampaigns.length}
          </p>
          <p className="mt-0.5 text-xs text-brown-400">campaigns delivered</p>
        </div>
        <div className="rounded-lg border border-brown-100 bg-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className="h-4 w-4 text-brown-300" />
            <span className="text-xs font-semibold uppercase tracking-wide text-brown-300">
              Scheduled
            </span>
          </div>
          <p className="font-display text-2xl font-semibold text-brown-500">
            {upcoming.length}
          </p>
          <p className="mt-0.5 text-xs text-brown-400">upcoming sends</p>
        </div>
      </div>}

      {/* Results (S5): what each e-mail brought in — saved nightly, beside Flow's leads and orders. */}
      <EmailResults onReady={setResultsReady} />

      {/* Upcoming sends */}
      {upcoming.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 px-6 py-4">
          <h3 className="flex items-center gap-2 font-display font-bold text-brown-500">
            <CalendarClock className="h-4 w-4 text-blue-600" />
            Upcoming sends
          </h3>
          <ul className="mt-3 space-y-2">
            {upcoming.map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-4 py-2.5 border border-blue-100"
              >
                <div>
                  <p className="text-sm font-semibold text-brown-500">
                    {c.name}
                  </p>
                  {c.audienceNames.length > 0 && (
                    <p className="text-xs text-brown-400">
                      to {c.audienceNames.join(", ")}
                    </p>
                  )}
                </div>
                {/* send_time is the planned delivery moment; scheduled_at is
                    just when the schedule button was clicked. */}
                <p className="text-sm font-semibold text-blue-700">
                  {c.sentAt || c.scheduledAt
                    ? fmtDateTime((c.sentAt ?? c.scheduledAt)!)
                    : "scheduled"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* AI draft panel — remounts (via key) when a history entry is loaded. */}
      {lists.length > 0 && (
        <DraftPanel
          key={`draft-${loaded?.nonce ?? 0}`}
          lists={lists}
          initial={loaded?.draft}
        />
      )}

      {/* Campaign performance */}
      {!resultsReady && campaigns.length > 0 && <CampaignTable campaigns={campaigns} />}

      {/* Lists */}
      {lists.length > 0 && (
        <div className="rounded-lg border border-brown-100 bg-white overflow-hidden">
          <div className="border-b border-brown-50 px-6 py-4">
            <h3 className="font-display font-bold text-brown-500">
              Audience lists
            </h3>
          </div>
          <ul className="divide-y divide-brown-50">
            {lists.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between px-6 py-3"
              >
                <div className="flex items-center gap-2.5">
                  <Mail className="h-3.5 w-3.5 text-brown-300" />
                  <p className="text-sm text-brown-500">{l.name}</p>
                </div>
                <p className="text-sm font-semibold text-brown-400">
                  {l.profileCount != null
                    ? `${l.profileCount.toLocaleString()} profiles`
                    : "—"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <HistoryDrawer
        kind="email"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onLoad={(d) =>
          setLoaded((prev) => ({ draft: d, nonce: (prev?.nonce ?? 0) + 1 }))
        }
      />
    </div>
  );
}

// ─── Klaviyo logo ─────────────────────────────────────────────────────────────

function KlaviyoLogo() {
  return (
    <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
      <path
        d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2zm0 4.667c1.654 0 2.995 1.34 2.995 2.993A2.997 2.997 0 0116 12.653a2.997 2.997 0 01-2.995-2.993A2.997 2.997 0 0116 6.667zm6.667 14.666H9.333v-2.666l4-4-4-4V8h2.667l4 4 4-4H22.667v2.667l-4 4 4 4v2.666z"
        fill="#1A1A1A"
      />
    </svg>
  );
}
